import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import moment from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL;

const TaskRow = ({
  task,
  activityIndex,
  stepIndex,
  taskIndex,
  handleChange,
  deleteItem,
  theme,
}) => (
  <TableRow hover>
    <TableCell>
      <TextField
        variant="standard"
        value={task.Title}
        onChange={(e) =>
          handleChange(
            activityIndex,
            stepIndex,
            taskIndex,
            "Title",
            e.target.value
          )
        }
        title={task.Title}
        fullWidth
        size="small"
        placeholder="Task Title"
      />
    </TableCell>
    <TableCell>
      <TextField
        variant="standard"
        value={task.Team}
        onChange={(e) =>
          handleChange(
            activityIndex,
            stepIndex,
            taskIndex,
            "Team",
            e.target.value
          )
        }
        fullWidth
        size="small"
        placeholder="Team"
      />
    </TableCell>
    <TableCell>
      <TextField
        variant="standard"
        type="date"
        value={task["Planned Date"]}
        onChange={(e) =>
          handleChange(
            activityIndex,
            stepIndex,
            taskIndex,
            "Planned Date",
            e.target.value
          )
        }
        size="small"
        fullWidth
      />
    </TableCell>
    <TableCell>
      <TextField
        variant="standard"
        type="time"
        value={task["Planned Start Time"]}
        onChange={(e) =>
          handleChange(
            activityIndex,
            stepIndex,
            taskIndex,
            "Planned Start Time",
            e.target.value
          )
        }
        size="small"
        fullWidth
      />
    </TableCell>
    <TableCell>
      <TextField
        variant="standard"
        type="time"
        value={task["Planned End Time"]}
        onChange={(e) =>
          handleChange(
            activityIndex,
            stepIndex,
            taskIndex,
            "Planned End Time",
            e.target.value
          )
        }
        size="small"
        fullWidth
      />
    </TableCell>
    <TableCell>
      <TextField
        variant="standard"
        value={task.requestNo}
        onChange={(e) =>
          handleChange(
            activityIndex,
            stepIndex,
            taskIndex,
            "requestNo",
            e.target.value
          )
        }
        fullWidth
        size="small"
        placeholder="CNG/RITM"
      />
    </TableCell>
    <TableCell align="center">
      <Tooltip title="Delete Task">
        <IconButton
          onClick={() => deleteItem(activityIndex, stepIndex, taskIndex)}
          size="small"
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </TableCell>
  </TableRow>
);

const StepPanel = ({
  step,
  activityIndex,
  stepIndex,
  handleChange,
  addTask,
  deleteItem,
  theme,
}) => (
  <Accordion
    defaultExpanded
    sx={{
      mb: 1.5,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      boxShadow: "none",
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{ bgcolor: "background.default" }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}
      >
        <TextField
          label={`Step ${stepIndex + 1} Title`}
          variant="standard"
          value={step.Title}
          onChange={(e) =>
            handleChange(
              activityIndex,
              stepIndex,
              null,
              "Title",
              e.target.value
            )
          }
          sx={{ flexGrow: 1 }}
        />
        <Tooltip title="Delete Step">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              deleteItem(activityIndex, stepIndex);
            }}
            size="small"
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </AccordionSummary>

    <AccordionDetails sx={{ p: 1.5 }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        className="enhanced-table-container"
        sx={{
          maxHeight: 300,
          borderRadius: 1,
        }}
      >
        <Table
          stickyHeader
          size="small"
          className="enhanced-table"
          aria-label="tasks table step-panel-table"
        >
          <TableHead className="enhanced-table-header">
            <TableRow>
              <TableCell sx={{ width: "45%" }}>
                Title
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                Team
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                Planned Date
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                Planned Start
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                Planned End
              </TableCell>
              <TableCell sx={{ width: "10%" }}>
                CNG/RITM
              </TableCell>
              <TableCell
                sx={{ width: "5%" }}
                align="center"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {step.Tasks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{
                    py: 2,
                    fontStyle: "italic",
                    color: "text.secondary",
                  }}
                >
                  No tasks yet. Click "Add Task" to add.
                </TableCell>
              </TableRow>
            ) : (
              step.Tasks.map((task, taskIndex) => (
                <TaskRow
                  key={taskIndex}
                  task={task}
                  activityIndex={activityIndex}
                  stepIndex={stepIndex}
                  taskIndex={taskIndex}
                  handleChange={handleChange}
                  deleteItem={deleteItem}
                  theme={theme}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={1} textAlign="right">
        <Button
          startIcon={<AddIcon />}
          onClick={() => addTask(activityIndex, stepIndex)}
          size="small"
          variant="text"
          color="primary"
        >
          Add Task
        </Button>
      </Box>
    </AccordionDetails>
  </Accordion>
);

const ActivityPanel = ({
  activity,
  activityIndex,
  handleChange,
  addStep,
  addTask,
  deleteItem,
  theme,
}) => (
  <Fade in={true} timeout={300}>
    <Accordion
      defaultExpanded
      sx={{
        mb: 2,
        borderRadius: 1,
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Activity {activityIndex + 1}
          </Typography>

          <TextField
            label="Activity Title"
            variant="standard"
            value={activity.Title}
            onChange={(e) =>
              handleChange(activityIndex, null, null, "Title", e.target.value)
            }
            sx={{ flexGrow: 1, ml: 1 }}
          />

          <Tooltip title="Delete Activity">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                deleteItem(activityIndex);
              }}
              size="small"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>
        {activity.Steps?.length === 0 && (
          <Typography color="text.secondary" mb={2}>
            No steps yet. Click "Add Step" to add.
          </Typography>
        )}

        {activity.Steps?.map((step, stepIndex) => (
          <StepPanel
            key={stepIndex}
            step={step}
            activityIndex={activityIndex}
            stepIndex={stepIndex}
            handleChange={handleChange}
            addTask={addTask}
            deleteItem={deleteItem}
            theme={theme}
          />
        )) || null}

        <Box mt={2} textAlign="right">
          <Button
            startIcon={<AddIcon />}
            onClick={() => addStep(activityIndex)}
            size="small"
            variant="outlined"
            color="primary"
          >
            Add Step
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  </Fade>
);

const DeploymentPlan = ({
  applicationNameProp,
  environmentProp,
  availableEnvironments = [],
}) => {
  const theme = useTheme();
  const { index, releaseName } = useParams();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState({
    header: { ApplicationName: "", color: "", comment: "" },
    activities: [],
  });

  console.log("App releaseName:", releaseName);

  // Application and environment state
  const [applicationName, setApplicationName] = useState(applicationNameProp);
  const [environments, setEnvironments] = useState(
    environmentProp && !Array.isArray(environmentProp)
      ? [environmentProp]
      : availableEnvironments.length > 0
      ? availableEnvironments
      : []
  );
  const [applications, setApplications] = useState([]);
  const [selectedEnv, setSelectedEnv] = useState(
    environmentProp ||
      (availableEnvironments.length > 0 ? availableEnvironments[0] : "")
  );

  // Other state variables
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [appLoading, setAppLoading] = useState(false);

  // Fetch all applications on component mount
  useEffect(() => {
    fetchAllApplications();
  }, []);

  // Effect to update state if props change
  useEffect(() => {
    if (applicationNameProp && applicationNameProp !== applicationName) {
      setApplicationName(applicationNameProp);
    }

    if (environmentProp) {
      // If environmentProp is a string, make sure it's in the environments array
      if (
        typeof environmentProp === "string" &&
        !environments.includes(environmentProp)
      ) {
        setEnvironments((prev) => [...prev, environmentProp]);
      }
      setSelectedEnv(environmentProp);
    }
  }, [applicationNameProp, environmentProp]);

  // Fetch applications when needed
  const fetchAllApplications = async () => {
    setAppLoading(true);
    try {
      const areaIndex = index !== undefined ? index : 0;
      const response = await axios.get(
        `${API_BASE}/areas/${areaIndex}/releases`
      );

      // Extract releases from the area data structure
      const releases = response.data.releases || [];
      setApplications(releases);

      // If we have an application name from props or URL, find its environments
      if (applicationName) {
        const selectedApp = releases.find(
          (app) => app.name === applicationName
        );
        if (selectedApp) {
          // If environmentProp exists, make sure it's in the list
          const envList = selectedApp.environments || [];
          if (environmentProp && !envList.includes(environmentProp)) {
            envList.push(environmentProp);
          }
          setEnvironments(envList);

          // Only set selectedEnv if not already set from props
          if (!selectedEnv && envList.length > 0) {
            setSelectedEnv(envList[0]);
          }
        } else {
          // If app not found by name, default to first app in the list
          if (releases.length > 0) {
            setApplicationName(releases[0].name);

            const envList = releases[0].environments || [];
            if (environmentProp && !envList.includes(environmentProp)) {
              envList.push(environmentProp);
            }
            setEnvironments(envList);

            if (!selectedEnv && envList.length > 0) {
              setSelectedEnv(environmentProp || envList[0]);
            }
          }
        }
      } else if (releases.length > 0) {
        // If no app name provided, default to first app
        setApplicationName(releases[0].name);

        const envList = releases[0].environments || [];
        if (environmentProp && !envList.includes(environmentProp)) {
          envList.push(environmentProp);
        }
        setEnvironments(envList);

        if (!selectedEnv && envList.length > 0) {
          setSelectedEnv(environmentProp || envList[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching releases:", error);
      setErrorMessage("Failed to load releases. Please try again later.");
    } finally {
      setAppLoading(false);
    }
  };

  // Handle application selection change
  const handleAppChange = (newValue) => {
    if (!newValue) return;

    setApplicationName(newValue.value);

    // Find the selected application and update environments
    const selectedApp = applications.find((app) => app.name === newValue.value);
    if (selectedApp) {
      const envList = selectedApp.environments || [];
      if (environmentProp && !envList.includes(environmentProp)) {
        envList.push(environmentProp);
      }
      setEnvironments(envList);

      if (envList.length > 0) {
        setSelectedEnv(environmentProp || envList[0]);
      } else {
        setSelectedEnv("");
      }
    }
  };

  const handleChange = useCallback(
    (activityIndex, stepIndex, taskIndex, field, value) => {
      setData((prev) => {
        const newData = { ...prev };
        if (taskIndex !== null && stepIndex !== null) {
          newData.activities[activityIndex].Steps[stepIndex].Tasks[taskIndex][
            field
          ] = value;
        } else if (stepIndex !== null && taskIndex === null) {
          newData.activities[activityIndex].Steps[stepIndex][field] = value;
        } else if (
          activityIndex !== null &&
          stepIndex === null &&
          taskIndex === null
        ) {
          newData.activities[activityIndex][field] = value;
        }
        return newData;
      });
    },
    []
  );

  const addActivity = useCallback(() => {
    setData((prev) => ({
      ...prev,
      activities: [...prev.activities, { Title: "", Steps: [] }],
    }));
  }, []);

  const addStep = useCallback((activityIndex) => {
    setData((prev) => {
      const newData = { ...prev };
      newData.activities[activityIndex].Steps.push({ Title: "", Tasks: [] });
      return newData;
    });
  }, []);
  const addTask = useCallback((activityIndex, stepIndex) => {
    const nyNow = moment.tz("America/New_York");
    const plannedDate = nyNow.format("YYYY-MM-DD");
    const plannedStartTime = nyNow.format("HH:mm");
    const plannedEndTime = nyNow.format("HH:mm");

    setData((prev) => {
      const newData = { ...prev };
      newData.activities[activityIndex].Steps[stepIndex].Tasks.push({
        Title: "",
        Team: "",
        "Planned Date": plannedDate,
        "Planned Start Time": plannedStartTime,
        "Planned End Time": plannedEndTime,
        "Start Time": "",
        "End Time": "",
        requestNo: "",
        Status: "Pending",
        StatusComment: "",
      });
      return newData;
    });
  }, []);

  const deleteItem = useCallback(
    (activityIndex, stepIndex = null, taskIndex = null) => {
      setData((prev) => {
        const newData = { ...prev };
        if (taskIndex !== null && stepIndex !== null) {
          newData.activities[activityIndex].Steps[stepIndex].Tasks.splice(
            taskIndex,
            1
          );
        } else if (stepIndex !== null && taskIndex === null) {
          newData.activities[activityIndex].Steps.splice(stepIndex, 1);
        } else if (
          activityIndex !== null &&
          stepIndex === null &&
          taskIndex === null
        ) {
          newData.activities.splice(activityIndex, 1);
        }
        return newData;
      });
    },
    []
  );

  const hasDuplicateTaskTitles = useCallback((activities) => {
    const titles = new Set();
    for (const activity of activities) {
      if (!activity.Steps) continue;
      for (const step of activity.Steps) {
        if (!step.Tasks) continue;
        for (const task of step.Tasks) {
          const title = (task.Title || "").trim().toLowerCase();
          if (!title) continue;
          if (titles.has(title)) return true;
          titles.add(title);
        }
      }
    }
    return false;
  }, []);

  const fetchData = useCallback(async () => {
    if (!applicationName || !selectedEnv) return;
    console.log("---------", applicationName, selectedEnv, releaseName);
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE}/Activities?AppName=${releaseName}`
      );
      setData(response.data || { header: {}, activities: [] });
    } catch (error) {
      console.error("Error fetching Activities:", error);
      setData({ header: {}, activities: [] });
    } finally {
      setLoading(false);
    }
  }, [applicationName, selectedEnv]);

  useEffect(() => {
    if (applicationName && selectedEnv) {
      fetchData();
    }
  }, [fetchData, applicationName, selectedEnv]);

  const handleSubmit = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (hasDuplicateTaskTitles(data.activities)) {
      setErrorMessage(
        "Duplicate task titles found. Please remove duplicates before saving."
      );
      return;
    }

    try {
      await axios.post(
        `${API_BASE}/Activities/Create?AppName=${releaseName}`,
        data,
        { headers: { "Content-Type": "application/json" } }
      );
      setSuccessMessage("Data saved successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchData();
    } catch (error) {
      console.error("Error updating activities:", error);
      setErrorMessage("Failed to save data. Please try again.");
    }
  };

  return (
    <Box className="releases-container">
      <Box>
        <Box className="releases-header">
          <Typography variant="h5" className="page-title">
            {searchParams.get("releaseName")} Plan - <span>{selectedEnv}</span>
          </Typography>
          <input
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            id="csv-upload"
            onChange={async (e) => {
              if (e.target.files && e.target.files[0]) {
                const formData = new FormData();
                formData.append("file", e.target.files[0]);
                console.log("Uploading file:", e.target.files[0]);
                try {
                  const resp = await axios.post(
                    `${API_BASE}/upload-csv`,
                    formData,
                    {
                      headers: {
                        "Content-Type": "multipart/form-data",
                      },
                    }
                  );
                  setData((prev) => ({
                    ...prev,
                    activities: resp.data.activities || [],
                  }));
                } catch (err) {
                  setErrorMessage("Failed to upload or parse CSV file.");
                }
              }
            }}
          />
          <label htmlFor="csv-upload">
            <Button
              component="span"
              variant="contained"
              size="small"
              sx={{ mt: 1 }}
            >
              Upload CSV
            </Button>
          </label>
        </Box>
      </Box>

      <Box className="deployment-content">
        {appLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 5,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress size={40} thickness={4} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading deployment data...
            </Typography>
          </Box>
        ) : (
          <>
            {data.activities.length === 0 && !appLoading && !loading && (
              <Paper
                elevation={0}
                sx={{
                  textAlign: "center",
                  py: 5,
                  px: 3,
                  bgcolor: "rgba(126, 211, 46, 0.08)",
                  border: "1px dashed",
                  borderColor: "primary.light",
                  borderRadius: 2,
                }}
              >
                <AssignmentIcon
                  sx={{ fontSize: 48, color: "primary.light", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No activities found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Create your deployment plan by adding activities, steps, and
                  tasks.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addActivity}
                  color="primary"
                  sx={{ borderRadius: 4 }}
                >
                  Add First Activity
                </Button>
              </Paper>
            )}

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  my: 3,
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <CircularProgress size={36} thickness={4} sx={{ mb: 1 }} />
                {/* <Typography variant="caption" color="text.secondary">
                      Loading/Updating...
                    </Typography> */}
              </Box>
            )}

            {data.activities.map((activity, index) => (
              <ActivityPanel
                key={index}
                activity={activity}
                activityIndex={index}
                handleChange={handleChange}
                addStep={addStep}
                addTask={addTask}
                deleteItem={deleteItem}
                theme={theme}
              />
            ))}

            {data.activities.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 3,
                  pt: 3,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
                className="deployment-footer"
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addActivity}
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    background:
                      "linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)",
                    px: 3,
                  }}
                >
                  Add Activity
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    background:
                      "linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)",
                    px: 3,
                  }}
                >
                  Submit Plan
                </Button>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              {errorMessage && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  {errorMessage}
                </Alert>
              )}
              {successMessage && (
                <Alert
                  severity="success"
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  {successMessage}
                </Alert>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DeploymentPlan;
